import React, { memo, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Checkbox } from "antd";
import { FieldWrapper } from "../FieldWrapper";
import {
  FieldComponentProps,
  CheckboxFieldConfig,
  FieldOption,
} from "models/formField";
import { getFieldError } from "helpers/formField.components.helper";
import styles from "./CheckboxField.module.scss";
import { parseHtml } from "@/utils/parseHtml.utils";

/**
 * CheckboxField - Multi-select checkbox component integrated with React Hook Form
 */
export const CheckboxField: React.FC<FieldComponentProps<CheckboxFieldConfig>> =
  memo(({ config, control: controlProp, errors: errorsProp }) => {
    // Get form context if available
    const formContext = useFormContext();
    const control = controlProp ?? formContext?.control;
    const errors = errorsProp ?? formContext?.formState?.errors;

    const error = getFieldError(errors, config.name);

    const isChecked = useCallback((selectedValues: any[], optionValue: any) => {
      return selectedValues.some(
        (val: any) => JSON.stringify(val) === JSON.stringify(optionValue),
      );
    }, []);

    if (!control) {
      console.error(
        `CheckboxField "${config.name}" requires control prop or FormProvider`,
      );
      return null;
    }

    return (
      <Controller
        name={config.name}
        control={control}
        defaultValue={config.defaultValue ?? []}
        rules={config.validation}
        render={({ field }) => {
          const selectedValues = Array.isArray(field.value) ? field.value : [];

          const handleCheckboxChange = (
            option: FieldOption,
            checked: boolean,
          ) => {
            const newValues = checked
              ? [...selectedValues, option.value]
              : selectedValues.filter(
                  (val: any) =>
                    JSON.stringify(val) !== JSON.stringify(option.value),
                );

            field.onChange(newValues);
            config.onChange?.(newValues);
          };

          return (
            <FieldWrapper
              label={config.label}
              name={config.name}
              error={error}
              tooltip={config.tooltip}
              required={!!config.validation?.required}
              className={config.className}
              hidden={config.hidden}
            >
              <div
                className={`${styles.checkboxGroup} ${
                  config.layout === "horizontal"
                    ? styles.horizontal
                    : styles.vertical
                }`}
                role="group"
                aria-labelledby={`${config.name}-label`}
              >
                {config.options.map((option, index) => {
                  return (
                    <div
                      key={`${config.name}_${index}`}
                      className={`${styles.checkboxItem} ${config.itemClassName || ""}`}
                    >
                      <Checkbox
                        id={`${config.name}_${index}`}
                        checked={isChecked(selectedValues, option.value)}
                        disabled={config.disabled || option.disabled}
                        onChange={(e) =>
                          handleCheckboxChange(option, e.target.checked)
                        }
                        className={styles.checkbox}
                      >
                        {parseHtml(option?.label)}
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            </FieldWrapper>
          );
        }}
      />
    );
  });

CheckboxField.displayName = "CheckboxField";
